// One audit engagement: team and segregation, the audit file sections, sign-offs,
// issuing the report, assembly deadline, post-issue additions and the event trail.
// The page shows what the server says; the server (database functions) enforces every rule.
import { useCallback, useEffect, useState } from 'react'
import {
  getAuditEngagement, assignAuditMember, removeAuditMember, setAuditOptions, setAuditSection,
  recordAuditSignoff, issueAuditReport, assembleAuditFile, addAuditAddition, approveAuditAddition,
  verifyAuditChain, listTeamMembers,
} from '../../api/kailux'
import { T } from '../../theme/theme'
import Icon from '../Icon'
import {
  ROLES, SIGNOFF_LEVELS, SECTION_STATUSES, STATUS_LABEL, basisLabel, roleLabel, signoffLabel, auditErrorText,
  currentSignoffs, sectionMap, sectionProgress, assemblyStatus, isLocked, formatDate, formatDateTime,
} from '../../utils/audit'
import { Card, Field, Pill, Notice, Spinline, btn, inputStyle, toneForStatus } from './ui'

export default function EngagementWorkspace({ engagementId, firmId, clientName, onBack }) {
  const [data, setData] = useState(null)
  const [team, setTeam] = useState([])
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [busy, setBusy] = useState(false)
  const [chain, setChain] = useState(null)

  const load = useCallback(async () => {
    try {
      const d = await getAuditEngagement(engagementId)
      setData(d)
    } catch (e) { setError(auditErrorText(e, 'Could not load the engagement.')) }
  }, [engagementId])
  useEffect(() => { load() }, [load])
  useEffect(() => { listTeamMembers(firmId).then((r) => setTeam(r.members || [])).catch(() => setTeam([])) }, [firmId])

  // run an action, show the server's own refusal message, then reload
  const act = async (fn, okMsg) => {
    setError(''); setInfo(''); setBusy(true)
    try { await fn(); if (okMsg) setInfo(okMsg); await load() }
    catch (e) { setError(auditErrorText(e)) }
    finally { setBusy(false) }
  }

  if (!data) return error ? <Notice tone="bad" testId="eng-error">{error}</Notice> : <Spinline />
  const { engagement: eng, members, sections, required_sections: required, signoffs, additions, events, blockers } = data
  const locked = isLocked(eng)
  const secMap = sectionMap(sections)
  const prog = sectionProgress(required, sections)
  const sign = currentSignoffs(signoffs)
  const asm = assemblyStatus(eng)
  const activeMembers = members.filter((m) => !m.ended_at)
  const nameOf = (fuId) => team.find((t) => t.id === fuId)?.email || fuId

  return (
    <div data-testid="engagement-workspace">
      <button type="button" onClick={onBack} style={{ ...btn(false), marginBottom: '14px' }}>Back to engagements</button>
      <Card title={`${clientName || 'Client'} — year ${eng.financial_year}`}
        right={<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Pill tone={toneForStatus(eng.status)} testId="eng-status">{STATUS_LABEL[eng.status]}</Pill>
          <Pill tone="muted">{basisLabel(eng.engagement_basis)}</Pill></div>}>
        <div style={{ fontSize: '13px', color: T.lt, display: 'grid', gap: '4px' }}>
          <div>Responsible Registered Auditor: <strong data-testid="eng-ra">{eng.responsible_name ? `${eng.responsible_name} (${eng.responsible_reg_no}), ${eng.audit_firm_name}` : 'Not designated'}</strong></div>
          {eng.report_date && <div>Report date: {formatDate(eng.report_date)} · File must be assembled by {formatDate(eng.assembly_due_at)} · Keep until {formatDate(eng.retention_until)}</div>}
        </div>
        {asm && asm.state !== 'done' && (
          <div style={{ marginTop: '10px' }}>
            <Notice tone={asm.state === 'ok' ? 'info' : 'warn'} testId="assembly-status">
              {asm.state === 'late' && 'The file was assembled after the 60-day deadline. This is recorded on the trail.'}
              {asm.state === 'overdue' && `The 60-day assembly deadline passed ${Math.abs(asm.days)} day(s) ago. Assemble the file now; the delay is recorded.`}
              {(asm.state === 'due_soon' || asm.state === 'ok') && `${asm.days} day(s) left to assemble the final file (60 calendar days from the report date).`}
            </Notice>
          </div>
        )}
      </Card>
      {error && <Notice tone="bad" testId="eng-error">{error}</Notice>}
      {info && <Notice tone="ok" testId="eng-info">{info}</Notice>}

      {!locked && (
        <Card testId="blockers-card" title="What is stopping the report from being issued">
          {blockers.length === 0
            ? <div style={{ fontSize: '13px', color: T.gn }} data-testid="no-blockers"><Icon name="checkCircle" /> Nothing. The responsible auditor can issue the report.</div>
            : <ul data-testid="blocker-list" style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: T.tx }}>
                {blockers.map((b, i) => <li key={`${b.code}-${i}`} data-code={b.code}>{b.message}</li>)}
              </ul>}
        </Card>
      )}

      <TeamCard eng={eng} members={activeMembers} team={team} locked={locked} busy={busy} nameOf={nameOf} act={act} />

      <Card testId="sections-card" title="Audit file" subtitle={`${prog.done} of ${prog.total} required sections completed. ${required.some((r) => r.reviewed_by_ra) ? '' : ' This section list is a draft that no Registered Auditor has reviewed yet.'}`}>
        {required.map((r) => {
          const s = secMap[r.section_key]
          return <SectionRow key={r.section_key} req={r} sec={s} locked={locked} busy={busy}
            onSave={(status, notes) => act(() => setAuditSection(eng.id, r.section_key, status, { notes }))} />
        })}
      </Card>

      <SignoffCard eng={eng} sign={sign} locked={locked} busy={busy} act={act} />

      {!locked && <IssueCard eng={eng} busy={busy} blockers={blockers} act={act} />}
      {eng.status === 'report_issued' && (
        <Card title="Assemble the final file" subtitle="Confirm the final audit file is complete. The server records whether this was inside the 60-day deadline.">
          <button type="button" disabled={busy} style={btn(true, busy)} onClick={() => act(() => assembleAuditFile(eng.id), 'File marked as assembled.')}>Mark file as assembled</button>
        </Card>
      )}
      {locked && <AdditionsCard eng={eng} additions={additions} required={required} busy={busy} act={act} />}

      <Card testId="trail-card" title="Event trail" subtitle="Every change is recorded in order and cannot be edited or removed. Each entry is chained to the one before it."
        right={<button type="button" style={btn(false)} onClick={async () => { try { setChain(await verifyAuditChain(eng.id)) } catch (e) { setError(auditErrorText(e)) } }}>Verify trail integrity</button>}>
        {chain && (chain.ok
          ? <Notice tone="ok" testId="chain-ok">Trail intact: {chain.checked} entries verified.</Notice>
          : <Notice tone="bad" testId="chain-broken">Trail integrity check FAILED at entry {chain.broken_seq}: {chain.reason}. Contact KaiLux support immediately.</Notice>)}
        <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: T.lt, maxHeight: '260px', overflowY: 'auto' }}>
          {events.map((ev) => <li key={ev.seq}>{formatDateTime(ev.created_at)} — {ev.event_type.replace(/_/g, ' ')} by {ev.actor_email || 'system'}</li>)}
        </ol>
      </Card>
    </div>
  )
}

function TeamCard({ eng, members, team, locked, busy, nameOf, act }) {
  const [who, setWho] = useState('')
  const [role, setRole] = useState('preparer')
  const [reason, setReason] = useState('')
  return (
    <Card testId="team-card" title="Team and independence"
      subtitle="Only a Registered Auditor with a current self-declaration can be the responsible auditor or the quality reviewer. The preparer and reviewer must be different people unless a reason is recorded.">
      <div data-testid="member-list">
        {members.map((m) => (
          <div key={m.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.bd}`, fontSize: '13px', color: T.tx }}>
            <span style={{ flex: 1 }}>{nameOf(m.firm_user_id)}</span><Pill tone="info">{roleLabel(m.role)}</Pill>
            {!locked && <button type="button" style={btn(false)} disabled={busy} onClick={() => act(() => removeAuditMember(eng.id, m.id))}>Remove</button>}
          </div>
        ))}
        {members.length === 0 && <div style={{ fontSize: '13px', color: T.mt }}>No one is assigned yet.</div>}
      </div>
      {!locked && (
        <>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '12px' }}>
            <div style={{ minWidth: '220px' }}><Field label="Person">
              <select aria-label="Person" value={who} onChange={(e) => setWho(e.target.value)} style={inputStyle}>
                <option value="">Select…</option>{team.map((t) => <option key={t.id} value={t.id}>{t.email}</option>)}
              </select></Field></div>
            <div style={{ minWidth: '220px' }}><Field label="Role">
              <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select></Field></div>
            <button type="button" disabled={busy || !who} style={btn(true, busy || !who)} onClick={() => act(() => assignAuditMember(eng.id, who, role), 'Assigned.')}>Assign</button>
          </div>
          <div style={{ marginTop: '14px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: '13px', color: T.tx, display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="checkbox" aria-label="EQR required" checked={!!eng.eqr_required}
                onChange={(e) => act(() => setAuditOptions(eng.id, { eqr_required: e.target.checked }))} />
              An engagement quality review is required
            </label>
            <div style={{ flex: 1, minWidth: '260px' }}><Field label="Segregation exception (same person prepares and reviews) — reason, at least 20 characters">
              <input aria-label="Segregation reason" value={reason || eng.segregation_exception_reason || ''} onChange={(e) => setReason(e.target.value)} style={inputStyle} /></Field></div>
            <button type="button" style={btn(false)} disabled={busy || reason.trim().length < 20}
              onClick={() => act(() => setAuditOptions(eng.id, { segregation_reason: reason }), 'Exception recorded.')}>Record exception</button>
          </div>
        </>
      )}
    </Card>
  )
}

function SectionRow({ req, sec, locked, busy, onSave }) {
  const [status, setStatus] = useState(sec?.status || 'not_started')
  const [notes, setNotes] = useState(sec?.content?.notes || '')
  useEffect(() => { setStatus(sec?.status || 'not_started'); setNotes(sec?.content?.notes || '') }, [sec])
  return (
    <div data-testid={`section-${req.section_key}`} style={{ padding: '10px 0', borderBottom: `1px solid ${T.bd}` }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '220px', fontSize: '13px', fontWeight: 700, color: T.tx }}>{req.label}</div>
        <select aria-label={`${req.label} status`} value={status} disabled={locked} onChange={(e) => setStatus(e.target.value)} style={{ ...inputStyle, width: '150px', marginTop: 0 }}>
          {SECTION_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {!locked && <button type="button" disabled={busy} style={btn(false)} onClick={() => onSave(status, notes)}>Save</button>}
      </div>
            <textarea aria-label={`${req.label} notes`} value={notes} disabled={locked} onChange={(e) => setNotes(e.target.value)} rows={2}
        placeholder="Working notes and conclusion for this section" style={{ ...inputStyle, resize: 'vertical' }} />
      {sec?.completed_by && <div style={{ fontSize: '11px', color: T.mt }}>Completed by {sec.completed_by}, {formatDateTime(sec.completed_at)}</div>}
    </div>
  )
}

function SignoffCard({ eng, sign, locked, busy, act }) {
  const [names, setNames] = useState({})
  return (
    <Card testId="signoffs-card" title="Sign-offs" subtitle="Each person signs in their own role. A later sign-off replaces an earlier one; the earlier one stays on record.">
      {SIGNOFF_LEVELS.filter((l) => l.value !== 'eqr' || eng.eqr_required).map((l) => {
        const cur = sign[l.value]
        return (
          <div key={l.value} data-testid={`signoff-${l.value}`} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', padding: '8px 0', borderBottom: `1px solid ${T.bd}` }}>
            <div style={{ minWidth: '230px', fontSize: '13px', fontWeight: 700, color: T.tx }}>{l.label}</div>
            <div style={{ flex: 1, fontSize: '12px', color: cur ? T.gn : T.mt }}>{cur ? `Signed by ${cur.signed_by_name}, ${formatDateTime(cur.signed_at)} (version ${cur.version})` : 'Not signed'}</div>
            {!locked && (
              <>
                <input aria-label={`${l.label} signer name`} placeholder="Your full name" value={names[l.value] || ''} onChange={(e) => setNames({ ...names, [l.value]: e.target.value })} style={{ ...inputStyle, width: '180px', marginTop: 0 }} />
                <button type="button" disabled={busy || !(names[l.value] || '').trim()} style={btn(true, busy || !(names[l.value] || '').trim())}
                  onClick={() => act(() => recordAuditSignoff(eng.id, l.value, names[l.value]), 'Signed.')}>Sign</button>
              </>
            )}
          </div>
        )
      })}
    </Card>
  )
}

function IssueCard({ eng, busy, blockers, act }) {
  const [date, setDate] = useState('')
  const [sure, setSure] = useState(false)
  const today = new Date().toISOString().slice(0, 10)
  return (
    <Card testId="issue-card" title="Issue the audit report"
      subtitle="Only the responsible Registered Auditor can issue. Issuing locks the audit file: after this, nothing can be changed, only added to with a recorded reason.">
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: '200px' }}><Field label="Report date"><input aria-label="Report date" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} /></Field></div>
        <label style={{ fontSize: '13px', color: T.tx, display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="checkbox" aria-label="I understand the file will be locked" checked={sure} onChange={(e) => setSure(e.target.checked)} /> I understand the file will be locked.
        </label>
        <button type="button" disabled={busy || !date || !sure || blockers.length > 0} style={btn(true, busy || !date || !sure || blockers.length > 0)}
          onClick={() => act(() => issueAuditReport(eng.id, date), 'Report issued and the file is locked.')}>Issue report</button>
      </div>
      {blockers.length > 0 && <div style={{ fontSize: '12px', color: T.mt, marginTop: '8px' }}>Resolve the items above first.</div>}
    </Card>
  )
}

function AdditionsCard({ eng, additions, required, busy, act }) {
  const [key, setKey] = useState('')
  const [text, setText] = useState('')
  const [reason, setReason] = useState('')
  const ok = key && text.trim() && reason.trim().length >= 10
  return (
    <Card testId="additions-card" title="Additions after the report" subtitle="The file is locked. Late evidence or corrections are added here with a reason and approved by the responsible auditor; the original stays untouched.">
      {additions.map((a) => (
        <div key={a.id} data-testid={`addition-${a.id}`} style={{ padding: '8px 0', borderBottom: `1px solid ${T.bd}`, fontSize: '13px', color: T.tx }}>
          <div><strong>{a.section_key.replace(/_/g, ' ')}</strong> — {a.reason}</div>
          <div style={{ fontSize: '12px', color: T.mt }}>Added by {a.added_by} on {formatDateTime(a.added_at)} · {a.approved_at ? `Approved by ${a.approved_by}` : 'Awaiting approval'}</div>
          {!a.approved_at && <button type="button" disabled={busy} style={{ ...btn(false), marginTop: '6px' }} onClick={() => act(() => approveAuditAddition(eng.id, a.id), 'Approved.')}>Approve (responsible auditor)</button>}
        </div>
      ))}
      <div style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
        <Field label="Section"><select aria-label="Addition section" value={key} onChange={(e) => setKey(e.target.value)} style={inputStyle}>
          <option value="">Select…</option>{required.map((r) => <option key={r.section_key} value={r.section_key}>{r.label}</option>)}</select></Field>
        <Field label="What is being added"><textarea aria-label="Addition content" rows={3} value={text} onChange={(e) => setText(e.target.value)} style={inputStyle} /></Field>
        <Field label="Reason (at least 10 characters)"><input aria-label="Addition reason" value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} /></Field>
        <div><button type="button" disabled={busy || !ok} style={btn(true, busy || !ok)}
          onClick={() => act(() => addAuditAddition(eng.id, key, { text }, reason), 'Addition recorded.').then(() => { setText(''); setReason('') })}>Add</button></div>
      </div>
    </Card>
  )
}
